import { TaskBoardModel } from "@jugl-web/rest-api/tasks/models/TaskBoard";
import { CreateTaskBoardPayload } from "@jugl-web/rest-api/tasks/models/TaskBoard/TaskBoardDto";
import { ManageBoardFormValues, taskObjectNamingPresets } from "./types";

export const transformBoardDataToFormValues = (
  board: TaskBoardModel
): ManageBoardFormValues => {
  const owners: string[] = [];
  const members: string[] = [];
  const viewers: string[] = [];

  board.users.forEach((user) => {
    if (user.type === "owner") {
      owners.push(user.id);
    } else if (user.type === "member") {
      members.push(user.id);
    } else if (user.type === "viewer") {
      viewers.push(user.id);
    }
  });

  const matchingTaskObjectNamingPreset = taskObjectNamingPresets.find(
    (preset) =>
      preset.singular === board.taskObjectNaming.singular &&
      preset.plural === board.taskObjectNaming.plural
  );

  const hasCustomTaskObjectNaming =
    !!board.taskObjectNaming.singular &&
    !!board.taskObjectNaming.plural &&
    !matchingTaskObjectNamingPreset;

  return {
    color: board.color,
    name: board.name,
    description: board.description,
    prefix: board.prefix,
    owners,
    members,
    viewers,
    taskObjectNamingId: hasCustomTaskObjectNaming
      ? "custom"
      : matchingTaskObjectNamingPreset?.id || "task",
    customTaskObjectNaming: {
      singular: hasCustomTaskObjectNaming
        ? board.taskObjectNaming.singular
        : "",
      plural: hasCustomTaskObjectNaming ? board.taskObjectNaming.plural : "",
    },
    includeBillingFiles: board.includeBillingFiles,
    includeOrderDetails: board.includeOrderDetails,
  };
};

export const transformFormValuesToBoardData = (
  data: ManageBoardFormValues
): CreateTaskBoardPayload => {
  const hasCustomTaskObjectNaming = data.taskObjectNamingId === "custom";

  const matchingTaskObjectNamingPreset = hasCustomTaskObjectNaming
    ? undefined
    : taskObjectNamingPresets.find(
        (preset) => preset.id === data.taskObjectNamingId
      );

  return {
    name: data.name,
    desc: data.description,
    color: data.color,
    prefix: data.prefix,
    users: [
      ...data.owners.map((id) => ({ id, type: "owner" as const })),
      ...data.members.map((id) => ({ id, type: "member" as const })),
      ...data.viewers.map((id) => ({ id, type: "viewer" as const })),
    ],
    settings: {
      include_order_details: data.includeOrderDetails,
      include_invoice_docs: data.includeBillingFiles,
      item_name_singular: hasCustomTaskObjectNaming
        ? data.customTaskObjectNaming.singular
        : matchingTaskObjectNamingPreset?.singular || null,
      item_name_plural: hasCustomTaskObjectNaming
        ? data.customTaskObjectNaming.plural
        : matchingTaskObjectNamingPreset?.plural || null,
    },
  };
};

export const DEFAULT_BOARD_COLOR = "#F33A9E";

export const getInitialManageBoardFormValues = (
  meId: string
): ManageBoardFormValues => ({
  color: DEFAULT_BOARD_COLOR,
  name: "",
  description: "",
  prefix: "",
  owners: [meId],
  members: [],
  viewers: [],
  taskObjectNamingId: "task",
  customTaskObjectNaming: {
    singular: "",
    plural: "",
  },
  includeBillingFiles: false,
  includeOrderDetails: false,
});
